/**
 * rebaseRecordFromServer (Path 2 "Re-apply my edits" — Phase 2, PURE
 * state-prep). Refreshes ONE record row's conflict basis from server truth:
 * point-read via the Phase-1b single-record get_field_records arm, then —
 * only after the verify gate passes — swap row.baseline and row.etag to the
 * fetched record. working, dirty, identity keys (record_id/tool_id/site_id),
 * cached_at, and ALL child arrays (part_numbers/extra_part_numbers/
 * asset_tags/extra_asset_tags/media) are NEVER touched: the user's pending
 * edits stay exactly as they were, now diffed against a fresh basis, and
 * buildCapturePayload picks the fresh etag off the row automatically on the
 * next enqueue.
 *
 * This function does NOT re-enqueue and does NOT delete the conflict queue
 * item — that wiring is Phase 4. No server write happens here (the
 * point-read is a read-only endpoint).
 *
 * Verify gate — strictly fetch-and-verify BEFORE any write; nothing is
 * persisted unless ALL pass:
 *   1. local row exists          → else { rebased:false, reason:'row_absent' }
 *      (a purged row has no working/dirty to overlay — not recoverable here)
 *   2. server record came back   → else { rebased:false, reason:'not_found' }
 *   3. fetched etag is a non-empty string
 *                                → else { rebased:false, reason:'no_etag' }
 */

import { fieldDb } from '../db/fieldDb';
import { ensureOpen, buildBaseline } from '../db/fieldStore';
import { fetchFieldRecord } from '../data/fetchFieldRecords';
import { deepEqual } from '../data/sameFieldValue';

export async function rebaseRecordFromServer(recordId, callerEmail) {
  await ensureOpen();
  const row = await fieldDb.records.get(recordId);
  if (!row) return { rebased: false, reason: 'row_absent' };

  const fetched = await fetchFieldRecord(row.site_id, recordId, callerEmail);
  if (!fetched) return { rebased: false, reason: 'not_found' };
  if (typeof fetched.etag !== 'string' || !fetched.etag.trim()) {
    return { rebased: false, reason: 'no_etag' };
  }

  row.baseline = buildBaseline(fetched);
  row.etag = fetched.etag;
  await fieldDb.records.put(row);
  return { rebased: true, row };
}

/**
 * Re-base probe (permanent verification artifact — __fieldQueueProbe
 * pattern). From the DevTools console on any /field page:
 *
 *   __fieldRebaseProbe('<record_id>', '<caller_email>')
 *
 * Snapshots the row's etag, structuredClones of working/dirty, and
 * representative baseline scalars BEFORE the re-base; runs
 * rebaseRecordFromServer; console.tables pre/post. Load-bearing assertion:
 * working and dirty must be DEEP-EQUAL pre vs post — a re-base must never
 * touch pending edits. Makes no server write and never enqueues. Inert
 * unless called; console access already implies IDB access.
 */

const PROBE_BASELINE_KEYS = ['supplier_tool_id', 'tool_type', 'inventory_comments'];

if (typeof window !== 'undefined') {
  window.__fieldRebaseProbe = async (recordId, callerEmail) => {
    await ensureOpen();
    const pre = await fieldDb.records.get(recordId);
    if (!pre) {
      // No local row — still run, to exercise the row_absent arm end-to-end.
      const result = await rebaseRecordFromServer(recordId, callerEmail);
      console.table([{ arm: 'no-local-row', rebased: result.rebased, reason: result.reason ?? '' }]);
      return result;
    }

    const preEtag = pre.etag;
    const preWorking = structuredClone(pre.working);
    const preDirty = structuredClone(pre.dirty);
    const preBaseline = {};
    for (const k of PROBE_BASELINE_KEYS) preBaseline[k] = pre.baseline?.[k];

    const result = await rebaseRecordFromServer(recordId, callerEmail);

    const post = await fieldDb.records.get(recordId);
    const workingEqual = deepEqual(preWorking, post?.working);
    const dirtyEqual = deepEqual(preDirty, post?.dirty);

    console.table([
      { field: 'etag', pre: preEtag, post: post?.etag },
      ...PROBE_BASELINE_KEYS.map((k) => ({
        field: `baseline.${k}`, pre: preBaseline[k], post: post?.baseline?.[k],
      })),
    ]);
    console.table([{
      rebased: result.rebased,
      reason: result.reason ?? '',
      working_deep_equal: workingEqual,
      dirty_deep_equal: dirtyEqual,
    }]);

    return { ...result, working_deep_equal: workingEqual, dirty_deep_equal: dirtyEqual };
  };
}
